// src/components/common/MorseBarcode.jsx

import { useMemo } from "react";
import { motion } from "framer-motion";
import { MORSE_MESSAGES } from "../../data/morseMessages";

// モールス信号コード表
const MORSE_CODE = {
  A: ".-",    B: "-...",  C: "-.-.",  D: "-..",
  E: ".",     F: "..-.",  G: "--.",   H: "....",
  I: "..",    J: ".---",  K: "-.-",   L: ".-..",
  M: "--",    N: "-.",    O: "---",   P: ".--.",
  Q: "--.-",  R: ".-.",   S: "...",   T: "-",
  U: "..-",   V: "...-",  W: ".--",   X: "-..-",
  Y: "-.--",  Z: "--..",
  "0": "-----", "1": ".----", "2": "..---", "3": "...--",
  "4": "....-", "5": ".....", "6": "-....", "7": "--...",
  "8": "---..", "9": "----.",
  "'": ".----.",
};

// テキストをモールス信号の記号配列に変換
const textToMorse = (text) => {
  const result = [];
  const upper = text.toUpperCase();

  for (let i = 0; i < upper.length; i++) {
    const char = upper[i];
    if (char === " ") {
      result.push({ type: "word-gap" });
    } else if (MORSE_CODE[char]) {
      const code = MORSE_CODE[char];
      for (let j = 0; j < code.length; j++) {
        result.push({ type: code[j] === "-" ? "dash" : "dot" });
        if (j < code.length - 1) {
          result.push({ type: "symbol-gap" });
        }
      }
      if (i < upper.length - 1 && upper[i + 1] !== " ") {
        result.push({ type: "char-gap" });
      }
    }
  }

  return result;
};

// 各要素のスタイル
const getStyle = (type, color, isVertical) => {
  const bg = { backgroundColor: color };
  switch (type) {
    case "dash":
      return isVertical
        ? <div className="w-full" style={{ height: "2px", ...bg }} />
        : <div className="h-full" style={{ width: "2px", ...bg }} />;
    case "dot":
      return isVertical
        ? <div className="w-full" style={{ height: "1px", ...bg }} />
        : <div className="h-full" style={{ width: "1px", ...bg }} />;
    case "symbol-gap":
      return isVertical
        ? <div style={{ height: "2px" }} />
        : <div style={{ width: "2px" }} />;
    case "char-gap":
      return isVertical
        ? <div style={{ height: "4px" }} />
        : <div style={{ width: "4px" }} />;
    case "word-gap":
      return isVertical
        ? <div style={{ height: "8px" }} />
        : <div style={{ width: "8px" }} />;
    default:
      return null;
  }
};

export default function MorseBarcode({
  orientation = "vertical",   // vertical | horizontal
  position = "fixed",         // fixed | sticky | relative
  placement = "left",         // left | right | top | bottom
  scroll = true,
  scrollSpeed = 30,           // 秒数（大きいほど遅い）
  scrollDirection = "down",   // down | up (vertical) / left | right (horizontal)
  messageIndex,               // 未指定=ランダム
  color = "#1f2937",          // デフォルト：gray-800相当
}) {
  // メッセージ選択（ランダムはマウント時に固定）
  const message = useMemo(() => {
    if (messageIndex !== undefined && MORSE_MESSAGES[messageIndex]) {
      return MORSE_MESSAGES[messageIndex];
    }
    return MORSE_MESSAGES[Math.floor(Math.random() * MORSE_MESSAGES.length)];
  }, [messageIndex]);

  const symbols = textToMorse(message.text);
  const content = [
    ...symbols, { type: "word-gap" },
    ...symbols, { type: "word-gap" },
    ...symbols, { type: "word-gap" },
    ...symbols, { type: "word-gap" },
  ];

  // スクロールアニメーション設定
  const isVertical = orientation === "vertical";
  const animateFrom = "0%";
  const animateTo = scrollDirection === "up" || scrollDirection === "left" ? "25%" : "-25%";

  const animateProps = scroll
    ? {
        animate: isVertical ? { y: [animateFrom, animateTo] } : { x: [animateFrom, animateTo] },
        transition: { duration: scrollSpeed, repeat: Infinity, ease: "linear" },
      }
    : {};

  // 配置クラス
  const placementClass = {
    left:   "left-0 top-0 h-screen w-4",
    right:  "right-0 top-0 h-screen w-4",
    top:    "top-0 left-0 w-screen h-4",
    bottom: "bottom-0 left-0 w-screen h-4",
  }[placement];

  const containerClass = `hidden min-[360px]:flex ${position} ${placementClass} overflow-hidden z-40 flex justify-center items-center`;

  const innerClass = isVertical
    ? "flex flex-col items-center gap-0.5 pt-2"
    : "flex flex-row items-center gap-0.5 pl-2";

  return (
    <div
      className={containerClass}
      title={`${message.text} — ${message.author}`}
    >
      <motion.div className={innerClass} {...animateProps}>
        {content.map((symbol, i) => (
          <div key={i} className={isVertical ? "w-4" : "h-4"}>
            {getStyle(symbol.type, color, isVertical)}
          </div>
        ))}
      </motion.div>
    </div>
  );
}
